import React, { useState, useEffect } from 'react';
import { 
  Eye, EyeOff, Search, X, ChevronDown,
  Users, Store, CreditCard, TrendingUp,
  Calendar, Map, BarChart2, PieChart,
  Menu, ArrowUpRight, ArrowDownRight,
  Activity, DollarSign, UserPlus, Plus, QrCode,
  LayoutDashboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { HelpdeskIcon } from '../components/HelpdeskIcon';
import { ChatModal } from '../components/ChatModal';
import { Tooltip } from '../components/Tooltip';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';
import { UpdateBadge } from '../components/UpdateBadge';
import { usePWAUpdate } from '../hooks/usePWAUpdate';
import { useCustomerSearch } from '../hooks/useCustomerSearch';
import QRCode from 'react-qr-code';

interface CustomerWithAvatar extends Profile {
  avatarUrl?: string;
  balance?: number;
}

interface Transaction {
  id: string;
  customer_smart_key: string;
  vendor_smart_key: string;
  amount: number;
  currency: string;
  payment_type: 'credit' | 'debit';
  remarks: string;
  created_at: string;
  customer?: Profile;
  vendor?: Profile;
}

interface TransactionTotals {
  debit: number;
  credit: number;
}

function Home() {
  const [showQuickAccessMenu, setShowQuickAccessMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithAvatar | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [avatarUrl, setAvatarUrl] = useState('https://images.unsplash.com/photo-1600486913747-55e5470d6f40');
  const [customers, setCustomers] = useState<CustomerWithAvatar[]>([]);
  const [todayTransactions, setTodayTransactions] = useState<Transaction[]>([]);
  const [displayedTransactions, setDisplayedTransactions] = useState<Transaction[]>([]);
  const [monthlyTotals, setMonthlyTotals] = useState<TransactionTotals>({ debit: 0, credit: 0 });
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDebitAmount, setShowDebitAmount] = useState(false);
  const [showCreditAmount, setShowCreditAmount] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(true);
  const navigate = useNavigate();

  const { performUpdate } = usePWAUpdate();

  const { 
    searchResults, 
    searching, 
    searchCustomers,
    syncQuickAccessToCache,
    setSearchResults 
  } = useCustomerSearch();

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const maskAmount = (amount: number) => {
    return '••••••';
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Refresh data when page comes back into focus
  useEffect(() => {
    const handleFocus = () => {
      if (profile?.smart_key) {
        fetchQuickAccess();
        fetchTransactions();
        fetchMonthlyTotals();
        fetchTotalBalance();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [profile?.smart_key]);

  useEffect(() => {
    if (profile?.smart_key) {
      fetchAllCustomers();
      fetchTransactions();
      fetchMonthlyTotals();
      fetchTotalBalance();
    }
  }, [profile?.smart_key]);

  useEffect(() => {
    setDisplayedTransactions(
      showAllTransactions ? todayTransactions : todayTransactions.slice(0, 5)
    );
  }, [todayTransactions, showAllTransactions]);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('لم يتم العثور على الملف الشخصي');

      setProfile(profile);
      setIsAdmin(profile.phone === '7331533033');
      
      if (profile.avatar_url) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('avatars')
          .getPublicUrl(profile.avatar_url);
        setAvatarUrl(publicUrl);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      navigate('/login');
    }
  };

  const fetchAllCustomers = async () => {
    try {
      if (!profile?.smart_key) return;

      setCustomersLoading(true);
      let vendorSmartKey: string;

      if (profile.account_type === 'shop_owner') {
        vendorSmartKey = profile.smart_key;
      } else if (profile.account_type === 'shop_assistant') {
        // Get vendor's smart key through assistant relationship
        const { data: assistantData } = await supabase
          .rpc('get_vendor_for_assistant');
        vendorSmartKey = assistantData;
      } else {
        throw new Error('Unauthorized access');
      }

      if (!vendorSmartKey) {
        throw new Error('Could not determine vendor');
      }

      // Get all customers linked to this vendor (not just quick access)
      const { data: relationships, error: relationshipsError } = await supabase
        .from('vendor_customer_relationships')
        .select('customer_smart_key')
        .eq('vendor_smart_key', vendorSmartKey);

      if (relationshipsError) throw relationshipsError;

      if (!relationships?.length) {
        setCustomers([]);
        return;
      }

      const customerSmartKeys = relationships.map(r => r.customer_smart_key);

      // Get profiles for all customers
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('smart_key', customerSmartKeys);

      if (profilesError) throw profilesError;

      // Fetch balance and avatar URL for each customer
      const customersWithDetails = await Promise.all(
        (profiles || []).map(async (customer) => {
          // Fetch balance only between current vendor and this customer
          const { data: transactions } = await supabase
            .from('transactions')
            .select('amount')
            .eq('vendor_smart_key', vendorSmartKey)
            .eq('customer_smart_key', customer.smart_key);

          const balance = transactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;

          // Get avatar URL if available
          let avatarUrl = customer.avatar_url;
          if (customer.avatar_url) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('avatars')
              .getPublicUrl(customer.avatar_url);
            avatarUrl = publicUrl;
          }

          return {
            ...customer,
            balance,
            avatarUrl
          };
        })
      );

      setCustomers(customersWithDetails);

      // Sync all customers to local cache for better performance
      if (customersWithDetails.length > 0) {
        await syncQuickAccessToCache(profile, customersWithDetails);
      }
    } catch (err) {
      console.error('Error fetching all customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchQuickAccess = async () => {
    try {
      if (!profile?.smart_key) return;

      // Get quick access entries for this user
      const { data: quickAccessEntries, error: quickAccessError } = await supabase
        .from('quick_access')
        .select('customer_smart_key')
        .eq('vendor_smart_key', profile.smart_key);

      if (quickAccessError) throw quickAccessError;

      if (!quickAccessEntries?.length) {
        setCustomers([]);
        return;
      }

      // Get profiles for all users in quick access
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('smart_key', quickAccessEntries.map(entry => entry.customer_smart_key));

      if (profilesError) throw profilesError;

      const profilesWithAvatarsAndBalance = await Promise.all(
        (profiles || []).map(async (profile) => {
          // Get avatar URL
          if (profile.avatar_url) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('avatars')
              .getPublicUrl(profile.avatar_url);
            
            // Calculate balance for this customer
            const { data: transactions } = await supabase
              .from('transactions')
              .select('amount')
              .eq('customer_smart_key', profile.smart_key);

            const balance = transactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;

            return { ...profile, avatarUrl: publicUrl, balance };
          } else {
            // Calculate balance for this customer
            const { data: transactions } = await supabase
              .from('transactions')
              .select('amount')
              .eq('customer_smart_key', profile.smart_key);

            const balance = transactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;

            return { ...profile, balance };
          }
        })
      );

      setCustomers(profilesWithAvatarsAndBalance);

      // Sync quick access customers to local cache
      if (profilesWithAvatarsAndBalance.length > 0) {
        await syncQuickAccessToCache(profile, profilesWithAvatarsAndBalance);
      }
    } catch (err) {
      console.error('Error fetching quick access:', err);
    }
  };

  const fetchMonthlyTotals = async () => {
    try {
      if (!profile?.smart_key) return;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

      let query = supabase
        .from('transactions')
        .select('amount, payment_type')
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (profile.account_type === 'shop_owner') {
        query = query.or(`vendor_smart_key.eq.${profile.smart_key},customer_smart_key.eq.${profile.smart_key}`);
      } else {
        query = query.eq('customer_smart_key', profile.smart_key);
      }

      const { data, error } = await query;

      if (error) throw error;

      const totals = (data || []).reduce((acc, transaction) => ({
        debit: acc.debit + (transaction.payment_type === 'debit' ? Math.abs(transaction.amount) : 0),
        credit: acc.credit + (transaction.payment_type === 'credit' ? Math.abs(transaction.amount) : 0)
      }), { debit: 0, credit: 0 });

      setMonthlyTotals(totals);
    } catch (err) {
      console.error('Error fetching monthly totals:', err);
    }
  };

  const fetchTotalBalance = async () => {
    try {
      if (!profile?.smart_key) return;

      let query = supabase
        .from('transactions')
        .select('amount, payment_type');

      if (profile.account_type === 'shop_owner') {
        query = query.eq('vendor_smart_key', profile.smart_key);
      } else {
        query = query.eq('customer_smart_key', profile.smart_key);
      }

      const { data, error } = await query;

      if (error) throw error;

      const total = (data || []).reduce((sum, transaction) => {
        if (profile.account_type === 'customer') {
          return sum + (transaction.amount * -1);
        }
        return sum + transaction.amount;
      }, 0);

      setTotalBalance(total);
    } catch (err) {
      console.error('Error fetching total balance:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      if (!profile?.smart_key) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayQuery = supabase
        .from('transactions')
        .select(`
          *,
          customer:profiles!transactions_customer_smart_key_fkey(
            username,
            avatar_url
          ),
          vendor:profiles!transactions_vendor_smart_key_fkey(
            username,
            avatar_url
          )
        `)
        .gte('created_at', today.toISOString())
        .order('created_at', { ascending: false });

      if (profile.account_type === 'shop_owner') {
       todayQuery = todayQuery.or(`vendor_smart_key.eq.${profile.smart_key},customer_smart_key.eq.${profile.smart_key}`);
      } else {
        todayQuery = todayQuery.eq('customer_smart_key', profile.smart_key);
      }

      const { data: todayData, error: todayError } = await todayQuery;

      if (todayError) throw todayError;

      setTodayTransactions(todayData || []);
      setDisplayedTransactions(showAllTransactions ? todayData || [] : (todayData || []).slice(0, 5));
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = async (value: string) => {
    setSearchQuery(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    if (profile) {
      await searchCustomers(value, profile);
    }
  };

  const handleAddToQuickAccess = async (selectedProfile: CustomerWithAvatar) => {
    try {
      if (!profile?.smart_key) return;

      // Create bidirectional quick access entries
      const entries = [
        {
          vendor_smart_key: profile.smart_key, // Current user's list
          customer_smart_key: selectedProfile.smart_key // Selected user in current user's list
        },
        {
          vendor_smart_key: selectedProfile.smart_key, // Selected user's list
          customer_smart_key: profile.smart_key // Current user in selected user's list
        }
      ];

      const { error } = await supabase
        .from('quick_access')
        .insert(entries);

      if (error) {
        if (error.code === '23514') {
          alert('يمكنك إضافة 6 عناصر كحد أقصى للوصول السريع. الرجاء إزالة بعض العناصر أولاً.');
        } else {
          throw error;
        }
        return;
      }

      await fetchQuickAccess();

      setShowQuickAccessMenu(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error adding to quick access:', err);
      alert('فشل في الإضافة إلى الوصول السريع');
    }
  };

  const handleRemoveFromQuickAccess = async (smartKey: string) => {
    try {
      if (!profile?.smart_key) return;

      // Remove bidirectional quick access entries
      const { error } = await supabase
        .from('quick_access')
        .delete()
        .or(`and(vendor_smart_key.eq.${profile.smart_key},customer_smart_key.eq.${smartKey}),and(vendor_smart_key.eq.${smartKey},customer_smart_key.eq.${profile.smart_key})`);

      if (error) throw error;

      await fetchQuickAccess();
    } catch (err) {
      console.error('Error removing from quick access:', err);
      alert('فشل في إزالة العنصر من الوصول السريع');
    }
  };

  const handleProfileClick = (selectedProfile: CustomerWithAvatar) => {
    navigate(`/customer/${selectedProfile.smart_key}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-6 pt-8 flex items-center justify-center">
        <div className="text-gray-500">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="px-6 pt-8 pb-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <ProfileAvatar
                  src={avatarUrl}
                  username={profile?.username || ''}
                  size="sm"
                />
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                >
                  <QrCode size={12} className="text-white" />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/update-profile')}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <Menu size={20} className="text-gray-600" />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="p-2 rounded-full hover:bg-gray-100 text-blue-600"
                    title="Dashboard"
                  >
                    <LayoutDashboard size={20} />
                  </button>
                )}
                {/* Update Badge */}
                <UpdateBadge onClick={performUpdate} />
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-gray-500">الرصيد الإجمالي</p>
                <Tooltip text="إجمالي الرصيد المستحق لك من جميع العملاء" />
              </div>
              <p className="text-xl font-bold">{formatAmount(totalBalance, profile?.currency || 'YER')} {profile?.currency}</p>
              <p className="text-xs text-gray-500 mt-1">Smart Key: {profile?.smart_key}</p>
            </div>
          </div>
          <button className="relative p-3 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group">
            <HelpdeskIcon 
              size={24} 
              className="text-blue-600 group-hover:text-purple-600 transition-colors duration-300" 
              onClick={() => setShowChatModal(true)}
            />
          </button>
        </div>

        {showQRCode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-sm rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Smart Key QR Code</h3>
                <button
                  onClick={() => setShowQRCode(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="bg-white p-4 rounded-xl flex flex-col items-center space-y-4">
                <QRCode
                  value={profile?.smart_key || ''}
                  size={200}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox={`0 0 256 256`}
                />
                <p className="text-sm text-gray-500">امسح الرمز للاتصال</p>
                <p className="font-mono text-sm">{profile?.smart_key}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar">
          <div className="flex-none w-[calc(100vw-48px)] sm:w-72 h-44 rounded-2xl p-6 relative bg-gradient-to-br from-blue-500 via-purple-400 to-pink-300 snap-start">
            <div className="flex justify-between items-start">
              <p className="text-white text-lg">الأجل</p>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6 object-contain" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-white text-3xl font-bold">
                {showDebitAmount 
                  ? formatAmount(monthlyTotals.debit, profile?.currency || 'YER')
                  : maskAmount(monthlyTotals.debit)
                }
              </p>
              <button 
                className="text-white opacity-80 hover:opacity-100 transition-opacity"
                onClick={() => setShowDebitAmount(!showDebitAmount)}
              >
                {showDebitAmount ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>
            <div className="flex justify-between items-end absolute bottom-6 left-6 right-6">
              <p className="text-white opacity-80">إجمالي الأجل للشهر الحالي</p>
              <p className="text-white opacity-80">{profile?.currency}</p>
            </div>
          </div>

          <div className="flex-none w-[calc(100vw-48px)] sm:w-72 h-44 rounded-2xl p-6 relative bg-gradient-to-br from-emerald-500 via-teal-400 to-cyan-300 snap-start">
            <div className="flex justify-between items-start">
              <p className="text-white text-lg">المبالغ المحصلة</p>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6 object-contain" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-white text-3xl font-bold">
                {showCreditAmount 
                  ? formatAmount(monthlyTotals.credit, profile?.currency || 'YER')
                  : maskAmount(monthlyTotals.credit)
                }
              </p>
              <button 
                className="text-white opacity-80 hover:opacity-100 transition-opacity"
                onClick={() => setShowCreditAmount(!showCreditAmount)}
              >
                {showCreditAmount ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>
            <div className="flex justify-between items-end absolute bottom-6 left-6 right-6">
              <p className="text-white opacity-80">إجمالي المبالغ المحصلة للشهر الحالي</p>
              <p className="text-white opacity-80">{profile?.currency}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold">
              العملاء
            </h2>
            <Tooltip 
              text="جميع العملاء المرتبطين بحسابك"
            />
          </div>
          <button className="text-gray-400">•••</button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar">
          {/* Add New Customer Button */}
          <div className="flex-none flex flex-col items-center space-y-2">
            <button 
              className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              onClick={() => navigate('/add-customer')}
            >
              <Plus size={28} className="text-white font-bold drop-shadow-sm" />
            </button>
            <p className="text-xs text-gray-700 font-medium text-center leading-tight">تسجيل عميل<br/>جديد</p>
          </div>

          {customers.map(customer => (
            <div 
              key={customer.smart_key} 
              className="flex-none flex flex-col items-center space-y-2 relative"
            >
              <ProfileAvatar
                src={customer.avatarUrl}
                username={customer.username}
                size="lg"
                onClick={() => handleProfileClick(customer)}
              />
              <div className="text-center">
                <p className="text-xs text-gray-600 font-medium">{customer.username.split(' ')[0]}</p>
                <p className="text-xs text-gray-500">
                  {customer.balance?.toFixed(2)} {customer.currency}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 mt-8 pb-24">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">اليوم</h2>
          <button className="text-gray-400">•••</button>
        </div>
        <div className="space-y-4">
          {displayedTransactions.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا توجد عمليات في هذه الفترة</p>
          ) : (
            displayedTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
              className="p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
              >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 mt-0.5">
                    <ProfileAvatar
                      src={profile?.account_type === 'shop_owner' 
                        ? transaction.customer?.avatar_url 
                          ? supabase.storage.from('avatars').getPublicUrl(transaction.customer.avatar_url).data.publicUrl 
                          : null
                        : transaction.vendor?.avatar_url
                          ? supabase.storage.from('avatars').getPublicUrl(transaction.vendor.avatar_url).data.publicUrl
                          : null
                      }
                      username={profile?.account_type === 'shop_owner' 
                        ? transaction.customer?.username || 'Unknown'
                        : transaction.vendor?.username || 'Unknown'
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900 truncate flex-1 min-w-0">
                        {profile?.account_type === 'shop_owner' 
                          ? transaction.customer?.username || 'Unknown Customer'
                          : transaction.vendor?.username || 'Unknown Vendor'
                        }
                      </p>
                      <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                       transaction.payment_type === 'debit'
                         ? 'bg-red-100 text-red-800'
                         : 'bg-green-100 text-green-800'
                      } whitespace-nowrap`}>
                       {profile?.account_type === 'shop_owner' ? (
                         transaction.payment_type === 'debit'
                           ? 'عليه'
                           : 'له'
                       ) : (
                         transaction.payment_type === 'debit'
                           ? 'لك'
                           : 'عليك'
                       )
                        }
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-gray-500 truncate flex-1 min-w-0">
                        {transaction.remarks || 'بدون ملاحظات'}
                      </p>
                      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                        {transaction.payment_type === 'debit' ? (
                          <ArrowDownRight size={14} className="text-red-500" />
                        ) : (
                          <ArrowUpRight size={14} className="text-green-500" />
                        )}
                        <span className={`font-semibold text-sm ${
                          transaction.payment_type === 'debit' 
                            ? 'text-red-600' 
                            : 'text-green-600'
                        }`}>
                          {formatAmount(transaction.amount, transaction.currency)} {transaction.currency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            ))
          )}
          {todayTransactions.length > 5 && (
            <button
              onClick={() => setShowAllTransactions(!showAllTransactions)}
              className="w-full py-3 text-blue-600 hover:text-blue-800 font-medium"
            >
              {showAllTransactions ? 'عرض أقل' : `عرض المزيد (${todayTransactions.length - 5})`}
            </button>
          )}
        </div>
      </div>

      {/* Add Transaction FAB */}
      <div className="fixed bottom-6 left-6 z-40">
        <div className="relative">
          {showFabMenu && (
            <div className="absolute bottom-16 left-0 space-y-3 mb-3">
              <button
                onClick={() => {
                  navigate('/add-customer');
                  setShowFabMenu(false);
                }}
                className="flex items-center space-x-3 bg-blue-500 bg-opacity-70 text-white rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 hover:bg-opacity-80"
              >
                <span className="font-medium">تسجيل عميل</span>
              </button>
              <button
                onClick={() => {
                  navigate('/top-up');
                  setShowFabMenu(false);
                }}
                className="flex items-center space-x-3 bg-green-500 bg-opacity-70 text-white rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 hover:bg-opacity-80"
              >
                <span className="font-medium">اضافة رصيد</span>
              </button>
              <button
                onClick={() => {
                  navigate('/add-transaction');
                  setShowFabMenu(false);
                }}
                className="flex items-center space-x-3 bg-red-500 bg-opacity-70 text-white rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 hover:bg-opacity-80"
              >
                <span className="font-medium">إضافة عملية</span>
              </button>
            </div>
          )}
          <button
            onClick={() => setShowFabMenu(!showFabMenu)}
            className={`w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform ${
              showFabMenu ? 'rotate-45 scale-110' : 'hover:scale-105'
            }`}
          >
            <Plus size={24} className="text-white" />
          </button>
        </div>
      </div>

      {/* Quick Access Menu Modal */}
      {showQuickAccessMenu && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">إضافة للوصول السريع</h3>
              <button
                onClick={() => {
                  setShowQuickAccessMenu(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="relative mb-4">
              <Search size={20} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="البحث عن المستخدمين..."
                value={searchQuery}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-3">
              {searching && (
                <div className="text-center py-4">
                  <div className="text-gray-500">جاري البحث...</div>
                </div>
              )}
              {searchResults.length === 0 && !searching && searchQuery.length >= 2 && (
                <div className="text-center py-4">
                  <div className="text-gray-500">لا توجد نتائج</div>
                </div>
              )}
              {searchResults.map((result) => (
                <div key={result.smart_key} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <ProfileAvatar
                      src={result.avatarUrl}
                      username={result.username}
                      size="sm"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{result.username}</p>
                      <p className="text-sm text-gray-500">{result.smart_key}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddToQuickAccess(result)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    إضافة
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal 
        isOpen={showChatModal} 
        onClose={() => setShowChatModal(false)} 
      />
    </div>
  );
}

export default Home;